import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Table, {
  RowData,
  SortInfo,
  ColumnOption,
} from '../../components/table/Table';
import { TripGroupView } from '../../types/tripGroup';
import { EditTripGroupPath } from '../../appRoutes';
import { toUTCLuxonDateTime } from '../../lib/date';
import { GreenCheckMark, RedXmark } from '../components/styledComponent';

interface SearchResultsListProps {
  headers: ColumnOption[];
  tripGroupData: TripGroupView[];
  loading: boolean;
  hasError: boolean;
  includeHidden: boolean;
  sortInfo: SortInfo;
  onSort?: (header: string) => void;
}

const LeftAlignLink = styled(Link)`
  text-align: left;
  display: block;
  margin: 10px;
`;

export const preSearchDummyId = '-1';

const getDisplayLineage = (tripGroup: TripGroupView) => {
  if (!tripGroup) {
    return <></>;
  } else {
    const { lineage, id } = tripGroup;
    const tripGroupPageLink = EditTripGroupPath.replace(':tripGroupId', id);
    const attributeArray = lineage.split(' | ');

    attributeArray?.[0]?.startsWith('ACCOUNT_ID:') && attributeArray.shift();

    // If this is the root lineage, display Default Account instead
    return (
      <LeftAlignLink key={`${id}-Link`} to={tripGroupPageLink}>
        {(!attributeArray?.length && <strong>Account Default</strong>) ||
          attributeArray.map(attributeValuePair => (
            <div key={`${id}-${attributeValuePair}`}>{attributeValuePair}</div>
          ))}
      </LeftAlignLink>
    );
  }
};

export const SearchResultsList: React.FC<SearchResultsListProps> = ({
  headers,
  tripGroupData,
  loading,
  hasError,
  includeHidden,
  sortInfo,
  onSort,
}) => {
  const hasSearched = !(
    tripGroupData.length === 1 && tripGroupData[0].id === preSearchDummyId
  );

  const noRowsMessage = loading
    ? 'loading'
    : hasSearched && !includeHidden
    ? 'No Trip Groups Found. Searching with “Include Hidden Groups” may provide additional results.'
    : hasSearched
    ? 'No Trip Groups Found'
    : 'Please Select Filters and Click Search to See Trip Groups';
  const errorMessage = 'Issue retrieving trip groups';

  const message = hasError
    ? errorMessage
    : !includeHidden &&
      hasSearched &&
      tripGroupData.length === 1 &&
      !tripGroupData[0].parentId
    ? 'Searching with “Include Hidden Groups” may provide additional results.'
    : '';

  const tableData: RowData[] = tripGroupData.map(tripGroup => {
    const alertProfileView = tripGroup.tripGroupViewSettings.alertProfileView;
    const hasCustomAlertSettings = !!alertProfileView;
    const canCreateTripAssistIssues =
      alertProfileView?.tripAssistSettings?.createIssues;
    const hasAgentTripAlerts =
      !!alertProfileView?.agentAlertSettings?.agentEmailWatchers?.length &&
      alertProfileView?.agentAlertSettings?.agentEmailWatchers.some(
        email => email.active,
      );
    const canSendTravelerTripAlerts = !!alertProfileView?.travelerAlertSettings
      ?.sendTravelerAlerts;

    const overriddenSettings = hasCustomAlertSettings
      ? [
          (canCreateTripAssistIssues && GreenCheckMark) || RedXmark,
          (hasAgentTripAlerts && GreenCheckMark) || RedXmark,
          (canSendTravelerTripAlerts && GreenCheckMark) || RedXmark,
        ]
      : // if no custom alert settings, leave blank rows instead of allowing fallback to Xs
        ['', '', ''];

    const created = toUTCLuxonDateTime(tripGroup.createAtUtc);
    return {
      key: tripGroup.id,
      values: [
        getDisplayLineage(tripGroup),
        tripGroup.tripGroupViewSettings.name,
        created.toFormat('LLL d y H:mm'),
        (hasCustomAlertSettings && GreenCheckMark) || RedXmark,
        ...overriddenSettings,
      ],
    };
  });

  return (
    <Table
      headers={headers}
      data={hasSearched ? tableData : []}
      noRowsValue={noRowsMessage}
      message={message}
      loading={loading}
      verticallyCenterCellContent={true}
      sortInfo={sortInfo}
      onSort={onSort}
    />
  );
};
